import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

import { Team as AppTeam, Organization as AppOrg } from '../../types';
import { Team as ContextTeam, Organization as ContextOrg } from '../../context/OrganizationContext';
import { useTeamPermissions } from '../../hooks/usePermissions';
import './utils/compatibility-fix';
import { ApiError } from '../../utils/error-handler';
import { formatDate } from '../../utils/date-formatter';
import ErrorDisplay from '../common/feedback/ErrorDisplay';
import { ledeSx } from '../../theme/styleHelpers';

interface TeamHeaderProps {
  team: AppTeam | ContextTeam;
  organization?: AppOrg | ContextOrg | null;
  onUpdate: (data: { name: string; description?: string }) => Promise<{ success: boolean; error?: ApiError }>;
}

const TeamHeader: React.FC<TeamHeaderProps> = ({ team, organization, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: team.name || '',
    description: team.description || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // Check permissions
  const teamId = (team as any)._id || (team as any).id;
  const { canUpdateTeam } = useTeamPermissions(teamId);

  const handleEditClick = () => {
    setEditData({
      name: team.name || '',
      description: team.description || ''
    });
    setIsEditing(true);
    setError(null);
  };

  const handleCloseEdit = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleChange = (field: 'name' | 'description') => (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditData(prev => ({ ...prev, [field]: e.target.value }));
    if (error) setError(null);
  };

  const handleSubmit = async () => {
    if (!editData.name || !editData.name.trim()) {
      setError({
        message: 'Team name is required',
        code: 'VALIDATION_ERROR',
        status: 400,
        field: 'name'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onUpdate({
        name: editData.name,
        description: editData.description || undefined
      });

      if (result.success) {
        setIsEditing(false);
      } else if (result.error) {
        setError(result.error);
      }
    } catch (err) {
      console.error('Failed to update team:', err);
      setError({
        message: 'Failed to update team',
        code: 'UNKNOWN_ERROR',
        status: 500
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const ledeText = [
    organization ? `Part of ${organization.name}` : null,
    team.description || null,
  ].filter(Boolean).join(' · ') || 'Manage team members, roles, and settings.';

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, marginBottom: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h1" sx={{
            fontSize: 32,
            fontWeight: 800,
            letterSpacing: '-0.025em',
            lineHeight: 1.15,
            marginBottom: 1,
          }}>
            {team.name || 'Team'}
          </Typography>
        </Box>
        {canUpdateTeam() && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pt: 0.5 }}>
            <Tooltip title="Edit Team">
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditClick}
                size="small"
              >
                Edit
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>
      <Typography sx={ledeSx}>
        {ledeText}
      </Typography>

      {/* Edit Dialog */}
      <Dialog open={isEditing} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Team
          <IconButton
            aria-label="close"
            onClick={handleCloseEdit}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Box sx={{ mt: 2 }}>
              <ErrorDisplay error={error} onClose={() => setError(null)} />
            </Box>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            id="name"
            label="Team Name"
            name="name"
            value={editData.name}
            onChange={handleChange('name')}
            error={error?.field === 'name'}
            disabled={isSubmitting}
            autoFocus
          />

          <TextField
            margin="normal"
            fullWidth
            id="description"
            label="Description"
            name="description"
            value={editData.description}
            onChange={handleChange('description')}
            error={error?.field === 'description'}
            multiline
            rows={3}
            disabled={isSubmitting}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={isSubmitting || !editData.name || !editData.name.trim()}
            startIcon={<SaveIcon />}
            variant="contained"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamHeader;